import React, { useState, useEffect, useRef, useCallback } from 'react';
import { clothingAPI, proxyClothingImageUrl } from '../api/client';
import ProductCard from '../components/ProductCard';
import ClothingProductModal from '../components/ClothingProductModal';
import { useAuthStore } from '../store';
import '../styles/home.css';
import '../styles/header.css';

const PAGE_SIZE = 48;
let resetGeneration = 0;

const _bustUrl = (url) => {
  if (!url) return url;
  try {
    const raw = localStorage.getItem('alldrop_photoTs');
    if (!raw) return url;
    const base = url.split('?')[0];
    const ts = JSON.parse(raw)[base];
    return ts ? `${base}?t=${ts}` : url;
  } catch { return url; }
};

// ── Tile component ─────────────────────────────────────────────────────────────

const BrowseTile = ({ name, photoUrl, onClick, label }) => {
  const [imgError, setImgError] = useState(false);
  const [bustVersion, setBustVersion] = useState(0);
  useEffect(() => {
    const handler = () => setBustVersion(v => v + 1);
    window.addEventListener('photo-rotated', handler);
    return () => window.removeEventListener('photo-rotated', handler);
  }, []);

  const rawSrc = photoUrl && !imgError
    ? (photoUrl.startsWith('http') ? proxyClothingImageUrl(photoUrl) : photoUrl)
    : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const src = React.useMemo(() => _bustUrl(rawSrc), [rawSrc, bustVersion]);

  return (
    <button className="browse-tile" onClick={onClick}>
      <div className="browse-tile-img-wrap">
        {src ? (
          <img
            src={src}
            alt={name}
            className="browse-tile-img"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="browse-tile-placeholder">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="browse-tile-name">{label || name}</div>
    </button>
  );
};

// ── Browse steps: 'type' → 'brand' → 'model' → 'products' ────────────────────

const ClothingPage = ({ onLogout, onAdmin, onSettings, onSwitchSection, canSwitchSection }) => {
  const [browseStep, setBrowseStep] = useState('type');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null); // {oc_code, photo_url, ...}

  const [navData, setNavData] = useState({ types: [], brands_by_type: {} });
  const [navLoading, setNavLoading] = useState(true);

  const [models, setModels] = useState([]);
  const [modelsTotal, setModelsTotal] = useState(0);
  const [modelsLimited, setModelsLimited] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Search-nav: filtered categories when search is active
  const [searchNavData, setSearchNavData] = useState(null); // {level, items} | null
  const [searchNavLoading, setSearchNavLoading] = useState(false);
  const searchNavTimerRef = useRef(null);

  const [sizes, setSizes] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [hideNoStock, setHideNoStock] = useState(false);

  const [search, setSearch] = useState('');

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [directProduct, setDirectProduct] = useState(null);

  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = currentUser?.is_admin;
  const isTeam = currentUser?.is_team && !currentUser?.is_admin;
  const hasRetail = !isAdmin && !isTeam && (currentUser?.permissions || []).some(
    p => ['retail_sites', 'retail_publish', 'retail_site_admin'].includes(p)
  );
  const initials = (currentUser?.username || 'П').charAt(0).toUpperCase();

  const skipRef = useRef(0);
  const hasMoreRef = useRef(true);
  const isLoadingRef = useRef(false);
  const currentGenRef = useRef(0);
  const sentinelRef = useRef(null);
  const searchTimerRef = useRef(null);
  const modelScrollRef = useRef(0);

  // ── Nav data ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    setNavLoading(true);
    clothingAPI.getNav()
      .then(r => setNavData(r.data || { types: [], brands_by_type: {} }))
      .catch(() => {})
      .finally(() => setNavLoading(false));

    const params = new URLSearchParams(window.location.search);
    const skuFromUrl = params.get('sku');
    if (skuFromUrl) {
      window.history.replaceState({}, '', window.location.pathname);
      clothingAPI.getBySku(skuFromUrl)
        .then(r => { if (r.data) setDirectProduct(r.data); })
        .catch(() => {});
    }
  }, []);

  // ── Sizes by type ─────────────────────────────────────────────────────────────

  useEffect(() => {
    setSizes([]);
    setSelectedSizes([]);
    clothingAPI.getSizes(selectedType || undefined)
      .then(r => setSizes(r.data || []))
      .catch(() => {});
  }, [selectedType]);

  // ── Load models ───────────────────────────────────────────────────────────────

  const loadModels = useCallback((type, brand) => {
    setModelsLoading(true);
    setModels([]);
    setModelsTotal(0);
    setModelsLimited(false);
    clothingAPI.getModels(type, brand)
      .then(r => {
        const data = r.data || {};
        setModels(data.models || []);
        setModelsTotal(data.total || 0);
        setModelsLimited(data.limited || false);
      })
      .catch(() => {})
      .finally(() => setModelsLoading(false));
  }, []);

  // ── Load products ─────────────────────────────────────────────────────────────

  const loadProducts = useCallback(async (reset = false) => {
    if (!reset && isLoadingRef.current) return;
    if (!reset && !hasMoreRef.current) return;

    isLoadingRef.current = true;

    if (reset) {
      resetGeneration += 1;
      currentGenRef.current = resetGeneration;
      skipRef.current = 0;
      hasMoreRef.current = true;
      setLoading(true);
      setLoadingMore(false);
    } else {
      setLoadingMore(true);
    }

    const myGen = currentGenRef.current;
    const skip = skipRef.current;

    const params = {};
    if (search.trim()) params.search = search.trim();
    if (selectedType) params.product_type = selectedType;
    if (selectedBrand) params.brand = selectedBrand;
    if (selectedModel) params.oc_code = selectedModel.oc_code;
    if (selectedSizes.length > 0) params.sizes = selectedSizes;
    if (hideNoStock) params.hide_no_stock = true;

    try {
      const response = await clothingAPI.list(skip, PAGE_SIZE, params);
      if (currentGenRef.current !== myGen) return;
      const newProducts = response.data;

      if (reset) setProducts(newProducts);
      else setProducts(prev => [...prev, ...newProducts]);

      skipRef.current = skip + newProducts.length;
      hasMoreRef.current = newProducts.length === PAGE_SIZE;
    } catch (error) {
      if (currentGenRef.current === myGen) console.error('Ошибка загрузки одежды:', error);
    } finally {
      if (currentGenRef.current === myGen) {
        isLoadingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [search, selectedType, selectedBrand, selectedModel, selectedSizes, hideNoStock]);

  useEffect(() => {
    if (browseStep === 'products') loadProducts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseStep, selectedType, selectedBrand, selectedModel, selectedSizes, hideNoStock]);

  // Infinite scroll
  useEffect(() => {
    if (browseStep !== 'products') return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadProducts(false); },
      { rootMargin: '800px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadProducts, browseStep]);

  useEffect(() => {
    if (loading) return;
    if (!hasMoreRef.current || isLoadingRef.current) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const { top } = sentinel.getBoundingClientRect();
    if (top < window.innerHeight + 300) loadProducts(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Restore scroll position when returning to model step
  useEffect(() => {
    if (browseStep === 'model' && modelScrollRef.current > 0) {
      const pos = modelScrollRef.current;
      requestAnimationFrame(() => window.scrollTo(0, pos));
    }
  }, [browseStep]);

  // Debounced search: products step → reload products; other steps → fetch search-nav
  useEffect(() => {
    if (browseStep === 'products') {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => loadProducts(true), 400);
      return () => clearTimeout(searchTimerRef.current);
    }

    // Non-products step: filter categories by search
    if (!search.trim()) {
      setSearchNavData(null);
      setSearchNavLoading(false);
      return;
    }
    clearTimeout(searchNavTimerRef.current);
    searchNavTimerRef.current = setTimeout(() => {
      setSearchNavLoading(true);
      const pt = (browseStep === 'brand' || browseStep === 'model') ? selectedType : undefined;
      const br = browseStep === 'model' ? selectedBrand : undefined;
      clothingAPI.searchNav(search.trim(), pt, br)
        .then(r => setSearchNavData(r.data || null))
        .catch(() => setSearchNavData(null))
        .finally(() => setSearchNavLoading(false));
    }, 300);
    return () => clearTimeout(searchNavTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, browseStep, selectedType, selectedBrand]);

  // Clear search-nav when search is cleared
  useEffect(() => {
    if (!search.trim()) setSearchNavData(null);
  }, [search]);

  // ── Navigation handlers ──────────────────────────────────────────────────────

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setSelectedBrand(null);
    setSelectedModel(null);
    setProducts([]);
    setSearchNavData(null);
    if (!search.trim()) {
      const brands = navData.brands_by_type[type] || [];
      setBrowseStep(brands.length === 0 ? 'products' : 'brand');
    } else {
      setBrowseStep('brand');
    }
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setSelectedModel(null);
    setProducts([]);
    setSearchNavData(null);
    loadModels(selectedType, brand);
    setBrowseStep('model');
  };

  const handleAllBrands = () => {
    setSelectedBrand(null);
    setSelectedModel(null);
    setProducts([]);
    setSearchNavData(null);
    loadModels(selectedType, null);
    setBrowseStep('model');
  };

  const handleModelSelect = (model) => {
    modelScrollRef.current = window.scrollY;
    setSelectedModel(model);
    setProducts([]);
    setBrowseStep('products');
  };

  const handleAllModels = () => {
    modelScrollRef.current = window.scrollY;
    setSelectedModel(null);
    setProducts([]);
    setBrowseStep('products');
  };

  const handleBack = () => {
    if (browseStep === 'products') {
      setProducts([]);
      if (selectedModel !== null) {
        setBrowseStep('model');
      } else if (models.length > 0 || selectedBrand !== null) {
        setBrowseStep('model');
      } else {
        const brands = navData.brands_by_type[selectedType] || [];
        if (brands.length > 0) setBrowseStep('brand');
        else { setBrowseStep('type'); setSelectedType(null); }
      }
    } else if (browseStep === 'model') {
      const brands = navData.brands_by_type[selectedType] || [];
      if (brands.length > 0) {
        setBrowseStep('brand');
      } else {
        setBrowseStep('type');
        setSelectedType(null);
      }
      setSelectedModel(null);
    } else if (browseStep === 'brand') {
      setBrowseStep('type');
      setSelectedType(null);
      setSelectedBrand(null);
    }
  };

  const handleRootClick = () => {
    setBrowseStep('type');
    setSelectedType(null);
    setSelectedBrand(null);
    setSelectedModel(null);
    setProducts([]);
  };

  // Enter в поиске: если в шаге категорий — прыгает сразу в товары (без фильтра по иерархии)
  const handleSearchGo = () => {
    if (!search.trim()) return;
    if (browseStep !== 'products') {
      setSelectedType(null);
      setSelectedBrand(null);
      setSelectedModel(null);
      setSearchNavData(null);
      setBrowseStep('products');
    }
  };

  const handleSearchClear = () => {
    setSearch('');
    if (browseStep === 'products' && !selectedType && !selectedBrand && !selectedModel) {
      setBrowseStep('type');
    }
  };

  // ── Breadcrumb ────────────────────────────────────────────────────────────────

  const crumbs = [];
  if (selectedType) crumbs.push({ label: selectedType, step: 'type' });
  if (selectedBrand) crumbs.push({ label: selectedBrand, step: 'brand' });
  if (selectedModel) crumbs.push({ label: selectedModel.measurements || selectedModel.oc_code, step: 'model' });

  const currentBrands = selectedType ? (navData.brands_by_type[selectedType] || []) : [];

  // ── Search-filtered tiles ─────────────────────────────────────────────────────

  const isSearchActive = search.trim().length > 0;

  // Types to show at type step
  const typeTiles = isSearchActive && searchNavData?.level === 'type'
    ? searchNavData.items.map(item => ({
        name: item.name,
        photo_url: navData.types.find(t => t.name === item.name)?.photo_url || null,
      }))
    : navData.types;

  // Brands to show at brand step
  const brandTiles = isSearchActive && searchNavData?.level === 'brand'
    ? searchNavData.items.map(item => ({
        name: item.name,
        photo_url: currentBrands.find(b => b.name === item.name)?.photo_url || null,
      }))
    : currentBrands;

  // Models to show at model step
  const modelTiles = isSearchActive && searchNavData?.level === 'model'
    ? searchNavData.items  // уже содержат {oc_code, photo_url, measurements}
    : models;

  const tileLoading = browseStep !== 'products' && isSearchActive && searchNavLoading;

  // ── Product title ─────────────────────────────────────────────────────────────

  const productTitle = selectedModel
    ? `${selectedModel.measurements || selectedModel.oc_code}${selectedBrand ? ` — ${selectedBrand}` : ''}`
    : selectedBrand
      ? selectedBrand
      : selectedType
        ? selectedType
        : search
          ? `Поиск: "${search}"`
          : 'Каталог одежды';

  return (
    <div className="home-page clothing-browse-page">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <img src="/logo.ico" alt="ALLDROP" className="logo-icon" />
            <h1>ALLDROP</h1>
          </div>

          <div className="header-search">
            <input
              type="text"
              className="header-search-input"
              placeholder="Поиск по названию, артикулу..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearchGo(); }}
            />
            {search && (
              <button className="search-clear" onClick={handleSearchClear} title="Очистить">×</button>
            )}
          </div>

          <div className="header-right">
            {canSwitchSection && (
              <button className="section-switch-btn" onClick={() => onSwitchSection('shoes')} title="Обувь">
                <span className="section-switch-label">Обувь</span>
                <span className="section-switch-icon">👟</span>
              </button>
            )}
            {isAdmin && (
              <button className="admin-panel-btn" onClick={onAdmin} title="Панель администратора">
                Управление
              </button>
            )}
            {isTeam && (
              <button className="admin-panel-btn admin-panel-btn--team" onClick={onAdmin} title="Аналитика">
                Аналитика
              </button>
            )}
            {hasRetail && (
              <button className="admin-panel-btn admin-panel-btn--team" onClick={onAdmin} title="Розница">
                Розница
              </button>
            )}
            <button className="settings-btn" onClick={onSettings} title="Настройки">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
            <button className="user-badge" onClick={onLogout} title="Выход">
              <span className="user-avatar">{initials}</span>
            </button>
          </div>
        </div>

        {browseStep === 'products' && (
          <div className="size-bar">
            {sizes.map(size => (
              <button
                key={size}
                className={`size-chip ${selectedSizes.includes(size) ? 'active' : ''}`}
                onClick={() => setSelectedSizes(prev =>
                  prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]
                )}
              >
                {size}
              </button>
            ))}
            {selectedSizes.length > 0 && (
              <button className="btn-clear" onClick={() => setSelectedSizes([])}>Сбросить</button>
            )}
            <button
              className={`size-chip size-chip--stock${hideNoStock ? ' active' : ''}`}
              onClick={() => setHideNoStock(v => !v)}
            >
              {hideNoStock ? '✓ Только в наличии' : 'Скрыть пустое наличие'}
            </button>
          </div>
        )}
      </header>

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <main className="main-content browse-main">

        {browseStep !== 'type' && (
          <div className="browse-breadcrumb">
            <button className="browse-back-btn" onClick={handleBack}>← Назад</button>
            <span className="browse-crumbs">
              <span className="browse-crumb-home" onClick={handleRootClick}>Одежда</span>
              {crumbs.map((crumb, i) => (
                <span key={crumb.label + i}>
                  <span className="browse-crumb-sep"> / </span>
                  <span
                    className={i === crumbs.length - 1 ? 'browse-crumb-active' : 'browse-crumb-link'}
                    onClick={i < crumbs.length - 1 ? () => {
                      if (crumb.step === 'type') { setBrowseStep('brand'); setSelectedBrand(null); setSelectedModel(null); }
                      if (crumb.step === 'brand') { setBrowseStep('model'); setSelectedModel(null); }
                    } : undefined}
                  >
                    {crumb.label}
                  </span>
                </span>
              ))}
            </span>
          </div>
        )}

        {/* ── Type tiles ──────────────────────────────────────────────────── */}
        {browseStep === 'type' && (
          <>
            <h2 className="browse-step-title">
              Выберите тип товара
              {isSearchActive && <span className="browse-search-badge">поиск: «{search}»</span>}
            </h2>
            {(navLoading || tileLoading) ? (
              <div className="loading"><div className="spinner" /></div>
            ) : typeTiles.length === 0 && isSearchActive ? (
              <div className="no-results">
                <p>Ничего не найдено по запросу «{search}».</p>
                <button className="btn btn-secondary" onClick={handleSearchGo}>Показать все совпадения</button>
              </div>
            ) : (
              <div className="browse-tiles browse-tiles--compact">
                {typeTiles.map(t => (
                  <BrowseTile
                    key={t.name}
                    name={t.name}
                    photoUrl={t.photo_url}
                    onClick={() => handleTypeSelect(t.name)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Brand tiles ─────────────────────────────────────────────────── */}
        {browseStep === 'brand' && (
          <>
            <h2 className="browse-step-title">
              Выберите бренд
              {isSearchActive && <span className="browse-search-badge">поиск: «{search}»</span>}
            </h2>
            {tileLoading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : brandTiles.length === 0 && isSearchActive ? (
              <div className="no-results">
                <p>Нет брендов с результатами для «{search}».</p>
                <button className="btn btn-secondary" onClick={() => { setSelectedBrand(null); setSelectedModel(null); setSearchNavData(null); setBrowseStep('products'); }}>
                  Показать все совпадения
                </button>
              </div>
            ) : (
              <div className="browse-tiles browse-tiles--compact">
                {!isSearchActive && (
                  <button className="browse-tile browse-tile--all" onClick={handleAllBrands}>
                    <div className="browse-tile-img-wrap browse-tile-all-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                        <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                      </svg>
                    </div>
                    <div className="browse-tile-name">Все бренды</div>
                  </button>
                )}
                {brandTiles.map(b => (
                  <BrowseTile
                    key={b.name}
                    name={b.name}
                    photoUrl={b.photo_url}
                    onClick={() => handleBrandSelect(b.name)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Model tiles ─────────────────────────────────────────────────── */}
        {browseStep === 'model' && (
          <>
            <h2 className="browse-step-title">
              Выберите модель
              {isSearchActive && <span className="browse-search-badge">поиск: «{search}»</span>}
              {modelsLimited && !isSearchActive && (
                <span className="browse-models-limit-badge">показано 120 из {modelsTotal} — выберите бренд для уточнения</span>
              )}
            </h2>
            {(modelsLoading || tileLoading) ? (
              <div className="loading"><div className="spinner" /></div>
            ) : modelTiles.length === 0 && isSearchActive ? (
              <div className="no-results">
                <p>Нет моделей с результатами для «{search}».</p>
                <button className="btn btn-secondary" onClick={() => { setSelectedModel(null); setSearchNavData(null); setBrowseStep('products'); }}>
                  Показать все совпадения
                </button>
              </div>
            ) : (
              <div className="browse-tiles">
                {!isSearchActive && (
                  <button className="browse-tile browse-tile--all" onClick={handleAllModels}>
                    <div className="browse-tile-img-wrap browse-tile-all-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                        <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                      </svg>
                    </div>
                    <div className="browse-tile-name">Все модели</div>
                  </button>
                )}
                {modelTiles.map(m => (
                  <BrowseTile
                    key={m.oc_code}
                    name={m.measurements || m.oc_code}
                    photoUrl={m.photo_url}
                    onClick={() => handleModelSelect(m)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Products grid ────────────────────────────────────────────────── */}
        {browseStep === 'products' && (
          <div className="products-section">
            <div className="section-header">
              <h2 className="section-title">{productTitle}</h2>
            </div>

            {loading && <div className="loading"><div className="spinner" /></div>}

            {!loading && products.length === 0 && (
              <div className="no-results">
                <p>Товары не найдены. Попробуйте изменить параметры.</p>
              </div>
            )}

            <div className="products-grid">
              {products.map((product, idx) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onSelect={() => setSelectedIndex(idx)}
                />
              ))}
            </div>

            <div ref={sentinelRef} className="scroll-sentinel" />

            {loadingMore && <div className="loading-more"><div className="spinner" /></div>}

            {!loadingMore && !hasMoreRef.current && products.length > 0 && (
              <div className="all-loaded">Все товары загружены ({products.length})</div>
            )}
          </div>
        )}
      </main>

      {directProduct && (
        <ClothingProductModal
          product={directProduct}
          onClose={() => setDirectProduct(null)}
          onPrev={null}
          onNext={null}
        />
      )}

      {!directProduct && selectedIndex !== null && products[selectedIndex] && (
        <ClothingProductModal
          product={products[selectedIndex]}
          onClose={() => setSelectedIndex(null)}
          onPrev={selectedIndex > 0 ? () => setSelectedIndex(i => i - 1) : null}
          onNext={selectedIndex < products.length - 1 ? () => setSelectedIndex(i => i + 1) : null}
        />
      )}
    </div>
  );
};

export default ClothingPage;
